import { z } from "zod";

// TODO If we update to use structured outputs, we can remove this
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Coercion function needs to accept any type
export const coerceToOptional = (v: any): any => {
  // Coerce all agent-generated nullish values to `undefined`
  if (
    v == null ||
    v === "" ||
    v === "null" ||
    v === "undefined" ||
    v === "None"
  ) {
    return undefined;
  }
  return v;
};

/**
 * The drop-in for runtypes' `Runtype.guard(value)`. A free function rather than
 * a method because zod has no equivalent on the schema itself.
 *
 * Narrows on success and never throws. The parsed value is discarded, so this
 * is a check and not a coercion: a schema carrying a transform or a default
 * still answers about the value it was handed.
 */
export function zGuard<S extends z.ZodTypeAny>(
  schema: S,
  value: unknown,
): value is z.infer<S> {
  return schema.safeParse(value).success;
}

/**
 * Any value except `null` and `undefined`, returned untouched: the zod form of
 * runtypes' `Record({})`, which is how opaque objects were declared. Unlike
 * `z.object({}).passthrough()` it does not reject a primitive and does not
 * copy the value, so it accepts and returns exactly what the runtype did.
 */
export function zAnyNonNullish(): z.ZodType<
  Record<string, unknown>,
  z.ZodTypeDef,
  unknown
> {
  return z.custom<Record<string, unknown>>((value) => value != null);
}

/** {@link InferPassthrough} applied to a type. */
type WithoutPassthroughIndex<T> = T extends
  | string
  | number
  | boolean
  | null
  | undefined
  | Date
  ? T
  : T extends ReadonlyArray<infer U>
    ? WithoutPassthroughIndex<U>[]
    : T extends object
      ? {
          [K in keyof T as string extends K
            ? unknown extends T[K]
              ? never
              : K
            : K]: WithoutPassthroughIndex<T[K]>;
        }
      : T;

/**
 * `z.infer` without the `[k: string]: unknown` that `.passthrough()` adds, at
 * any depth. With it, `Omit`/`Pick`/`keyof` see only the index signature and
 * the type stops checking against external ones (e.g. `V1Pod`). Real
 * `z.record` maps and `Date`s are kept.
 */
export type InferPassthrough<S extends z.ZodTypeAny> = WithoutPassthroughIndex<
  z.infer<S>
>;

/**
 * A number, `NaN` included -- what runtypes' `Number` accepted. `z.number()`
 * rejects `NaN`, but kernel dynamic values are written with `allow_nan` and
 * read back through JSON5, so a dataframe with a missing numeric cell carries
 * one. Use this wherever such values are validated.
 */
// eslint-disable-next-line hex/no-non-json-zod-in-wire-schema -- these values arrive through JSON5, which carries NaN; z.nan() only widens a number union
export const NumberOrNaN = z.number().or(z.nan());

/**
 * `z.record` keyed by a branded string. Plain `z.record` can't tell that a
 * branded key is still a string, so it infers `Partial<Record<K, V>>` and makes
 * every value possibly-undefined; this keeps the `Record<K, V>` shape that the
 * hand-written types these schemas replace already had.
 */
export function zBrandedRecord<K extends string, V extends z.ZodTypeAny>(
  value: V,
): z.ZodType<Record<K, z.infer<V>>, z.ZodTypeDef, Record<K, z.input<V>>> {
  return z.record(z.string(), value);
}

/**
 * A value validated only as an object, for the types that are external or
 * code-generated and so have no schema of their own. Nothing inside
 * is checked; the declared type is asserted, so callers still get `T` and
 * `ZodShapeFor` can hold a surrounding schema to its interface.
 *
 * Reach for this only where a real schema is out of reach -- it buys a type, not
 * validation. Prefer describing the shape whenever the source is hand-written.
 */
export function zUnvalidatedObject<T>(): z.ZodType<T, z.ZodTypeDef, unknown> {
  return z.record(z.string(), z.unknown()) as z.ZodType<
    T,
    z.ZodTypeDef,
    unknown
  >;
}

/**
 * {@link zUnvalidatedObject} for a value whose top level is an array. Needed as its
 * own helper because the two are not interchangeable: `z.record` rejects an array and
 * `z.array` rejects an object, so an array-shaped type run through
 * `zUnvalidatedObject` would fail the one check either helper still performs.
 */
export function zUnvalidatedArray<T>(): z.ZodType<T, z.ZodTypeDef, unknown> {
  // `as unknown as` rather than the plain `as` its sibling above uses, which is not an
  // oversight: `ZodRecord`'s output overlaps an arbitrary object type enough for a
  // direct assertion, while `ZodArray<ZodUnknown>` and `ZodType<T>` do not overlap for
  // an arbitrary `T`, so the compiler rejects it.
  return z.array(z.unknown()) as unknown as z.ZodType<T, z.ZodTypeDef, unknown>;
}

/**
 * `z.array` whose inferred type is `readonly T[]`, without freezing at parse
 * time.
 *
 * runtypes' `.asReadonly()` was purely type-level. zod's `.readonly()` calls
 * `Object.freeze` on the parsed value, which would turn a later mutation into a
 * silent no-op or a throw, so it is not a drop-in replacement -- this is.
 */
export function zReadonlyArray<V extends z.ZodTypeAny>(
  value: V,
): z.ZodType<readonly z.infer<V>[], z.ZodTypeDef, readonly z.input<V>[]> {
  return z.array(value);
}

/**
 * `Dictionary`'s own rule: a required value gives the full `Record<K, V>`, an
 * `Optional` one gives `Partial<Record<K, V>>`.
 */
type KeyedRecord<K extends string | number | symbol, V> = undefined extends V
  ? Partial<Record<K, V>>
  : Record<K, V>;

/**
 * The drop-in for `z.record(Key, Value)`. `z.record` widens to
 * `Partial<Record<K, V>>` for any key narrower than `string` -- a brand or a
 * `z.enum` alike -- which makes every value possibly-undefined; the hand-written
 * types these schemas stand in for infer the full `Record<K, V>` when the value is
 * required. Keys are still validated against `key`, so only the inferred type moves.
 */
export function zKeyedRecord<
  K extends z.ZodType<string, z.ZodTypeDef, string>,
  V extends z.ZodTypeAny,
>(
  key: K,
  value: V,
): z.ZodType<
  KeyedRecord<z.infer<K>, z.infer<V>>,
  z.ZodTypeDef,
  KeyedRecord<z.input<K>, z.input<V>>
> {
  return z.record(key, value);
}
